import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Lock } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"

/**
 * Si el servidor negó algo porque la cuenta tiene PIN y esta sesión no la ha abierto.
 *
 * ⚠ **No es un error y no se pinta como tal.** El servidor responde 409 con
 * `CUENTA_BLOQUEADA` (`services/pin_cuentas.py`), y Movimientos lo enseñaba como «No se
 * pudieron cargar los movimientos»: parecía una falla, cuando lo único que faltaba era
 * el PIN (lo reportó el usuario el 09/10).
 */
export function esCuentaBloqueada(error: unknown): boolean {
  return error instanceof ErrorApi && error.detalles?.motivo === "CUENTA_BLOQUEADA"
}

/** Lo que se enseña en lugar del saldo o del extracto de una cuenta cerrada. */
export function AvisoCuentaBloqueada({ nombre, onAbrir }: { nombre: string; onAbrir: () => void }) {
  return (
    <div className="tarjeta flex flex-col items-center gap-3 py-12 text-center">
      <Lock size={22} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
      <p className="text-sm font-medium">{nombre} está protegida con PIN</p>
      <p className="text-xs" style={{ color: "var(--fg-muted)" }}>
        Se pide una vez por sesión. Sin él no se ve el saldo, ni el extracto, ni se paga desde ella.
      </p>
      <Boton variante="primario" onClick={onAbrir}>
        <Lock size={16} aria-hidden={true} /> Abrir cuenta
      </Boton>
    </div>
  )
}

/**
 * El PIN tecleado ALLÍ MISMO, dentro del modal que lo necesitó (19/10, pedido del
 * usuario: «no tener que cambiar de pestaña»). Se pinta en lugar del error
 * `CUENTA_BLOQUEADA` y abre la cuenta que el servidor nombró en `detalles.cuenta_id`.
 *
 * ⚠ **En línea, no en otro modal**: el modal escucha Escape en `document`, y dos
 * abiertos a la vez se cerrarían con una sola pulsación. Mismo PIN, misma ruta y
 * mismos intentos que `DialogoPin`.
 *
 * Fuera de ese error no pinta nada: quien lo usa enseña sus otros errores como siempre.
 */
export function PinEnLinea({ error, onAbierta }: { error: unknown; onAbierta?: () => void }) {
  const clienteQuery = useQueryClient()
  const [pin, setPin] = useState("")
  const cuentaId = esCuentaBloqueada(error)
    ? String((error as ErrorApi).detalles?.cuenta_id ?? "")
    : ""
  const abrir = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/cuentas-bancarias/${cuentaId}/desbloquear`, { pin: pin.trim() }),
    onSuccess: () => {
      setPin("")
      // Todo lo que el servidor negaba mientras estaba cerrada.
      for (const clave of [
        "resumen-cuenta",
        "movimientos",
        "saldo-cuenta",
        "estado-cuenta",
        "propuesta-pago",
        "propuesta-pago-combinado",
        "cuentas-bancarias",
      ]) {
        clienteQuery.invalidateQueries({ queryKey: [clave] })
      }
      onAbierta?.()
    },
  })
  if (cuentaId === "") return null
  const fallo = abrir.error instanceof ErrorApi ? abrir.error : null
  const id = `pin-en-linea-${cuentaId}`
  return (
    <div className="tarjeta space-y-2 p-3 text-sm">
      <p className="flex items-center gap-2">
        <Lock size={16} aria-hidden={true} style={{ color: "var(--fg-muted)" }} />
        Esta cuenta pide su PIN para abrirse. Se pide una vez por sesión.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <label htmlFor={id} className="min-w-32 flex-1">
          <span className="mb-1 block text-xs font-medium uppercase">PIN</span>
          <input
            id={id}
            type="password"
            inputMode="numeric"
            autoComplete="off"
            value={pin}
            onChange={(e) => setPin(e.target.value)}
            onKeyDown={(e) => {
              // Enter abre la cuenta, no envía el formulario del pago que lo rodea.
              if (e.key === "Enter") {
                e.preventDefault()
                if (pin.trim().length >= 4) abrir.mutate()
              }
            }}
            maxLength={32}
            className="campo w-full tabular-nums"
          />
        </label>
        <Boton
          variante="primario"
          disabled={abrir.isPending || pin.trim().length < 4}
          onClick={() => abrir.mutate()}
        >
          <Lock size={16} aria-hidden={true} />
          {abrir.isPending ? "Abriendo…" : "Abrir cuenta"}
        </Boton>
      </div>
      {fallo && (
        <p role="alert" style={{ color: "var(--destructive)" }}>
          {fallo.message}
          {typeof fallo.detalles?.restantes === "number" && (
            <> · quedan {String(fallo.detalles.restantes)} intentos</>
          )}
        </p>
      )}
    </div>
  )
}
